// Portfolio display currency (ADR-0014). The portfolio ALWAYS opens in USD (owner decision 2026-07-21 —
// the "most common quote currency" default was dropped); the user can switch to any supported currency and
// every displayed amount converts at spot. Conversion is display-only: books, ledgers, and funding stay in
// each strategy's own quote currency.
//
// Store pattern mirrors lib/venueContext.js: a writable + a guarded setter; rates are a separate writable
// the view refreshes from the quote endpoint (BTC-USD / ETH-USD spot; USD/USDC par).

import { writable, get } from 'svelte/store';
import { SUPPORTED_QUOTE_CURRENCIES, DEFAULT_QUOTE_CURRENCY } from './quoteCurrency.js';

/** The currency all portfolio amounts display in. Always USD on load (owner decision). */
export const displayCurrency = writable(DEFAULT_QUOTE_CURRENCY);

export function setDisplayCurrency(currency) {
  const c = String(currency ?? '').toUpperCase();
  if (SUPPORTED_QUOTE_CURRENCIES.includes(c)) displayCurrency.set(c);
}

/**
 * USD spot per currency unit: { USD: 1, USDC: 1, BTC: <BTC-USD last>, ETH: <ETH-USD last> }.
 * BTC/ETH are null until a quote read succeeds — conversions needing a missing rate return null
 * (the view renders '—', never a silently-wrong number).
 */
export const usdRates = writable({ USD: 1, USDC: 1, BTC: null, ETH: null });

/** Refresh BTC/ETH spot via the quote endpoint. `getQuote(symbol)` → { lastPrice } (or lastTradePrice). */
export async function refreshRates(getQuote) {
  const next = { ...get(usdRates), USD: 1, USDC: 1 };
  for (const asset of ['BTC', 'ETH']) {
    try {
      const q = await getQuote(`${asset}/USD`);
      const price = Number(q?.lastPrice ?? q?.lastTradePrice ?? q?.price);
      next[asset] = Number.isFinite(price) && price > 0 ? price : next[asset];
    } catch {
      // keep the previous rate (possibly null) — a failed refresh must not zero a good rate
    }
  }
  usdRates.set(next);
  return next;
}

/**
 * Convert `amount` from one supported currency to another via USD cross rates.
 * Returns null when a needed rate is unknown — the caller renders '—', never a wrong number.
 */
export function convertAmount(amount, from, to, rates) {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return null;
  const f = String(from ?? '').toUpperCase();
  const t = String(to ?? '').toUpperCase();
  if (f === t) return Number(amount);
  const fromUsd = rates?.[f];
  const toUsd = rates?.[t];
  if (!Number.isFinite(fromUsd) || !Number.isFinite(toUsd) || toUsd === 0) return null;
  return (Number(amount) * fromUsd) / toUsd;
}

/**
 * Convert-and-sum a per-currency amount map (the server's navByCurrency) into one display-currency total.
 * Null when ANY bucket's rate is unknown — a partial sum would misstate the portfolio.
 */
export function sumInCurrency(amountsByCurrency, to, rates) {
  if (!amountsByCurrency) return null;
  let total = 0;
  for (const [currency, amount] of Object.entries(amountsByCurrency)) {
    const converted = convertAmount(amount, currency, to, rates);
    if (converted === null) return null;
    total += converted;
  }
  return total;
}
